'use client';

// P29.6 / Q7: marketing hero with framer-motion staggered reveal + 2 CTAs.
// Uses `m` (LazyMotion-compatible) so the bundle stays small via the
// LazyMotionProvider in (marketing)/layout.tsx. Respects useReducedMotion.

import Link from 'next/link';
import { m, useReducedMotion } from 'framer-motion';
import { useLocale, useTranslations } from 'next-intl';
import { trackEvent } from '@/lib/analytics/plausible';

function fillVars(template: string, vars: Record<string, string>): string {
  let out = template;
  for (const [k, v] of Object.entries(vars)) out = out.replaceAll(`%${k}%`, v);
  return out;
}

function trackCta(variant: 'hero-primary' | 'hero-secondary') {
  trackEvent('CTA Click', { variant });
}

export function HeroSection() {
  const t = useTranslations('marketing.hero');
  const locale = useLocale();
  const reduceMotion = useReducedMotion();
  const localePrefix = `/${locale}`;

  const headline = fillVars(t('headline'), { productName: t('productName') });
  const initial = reduceMotion ? false : { opacity: 0, y: 20 };
  const animate = { opacity: 1, y: 0 };

  return (
    <section
      data-testid="marketing-hero"
      className="relative isolate flex min-h-[80vh] items-center overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-emerald-100"
    >
      <div className="mx-auto w-full max-w-6xl px-4 py-16">
        <m.p
          initial={initial}
          animate={animate}
          transition={{ duration: 0.4, delay: 0 }}
          className="mb-3 text-xs font-medium tracking-widest text-emerald-700 uppercase"
        >
          {t('eyebrow')}
        </m.p>
        <m.h1
          initial={initial}
          animate={animate}
          transition={{ duration: 0.45, delay: 0.1 }}
          className="text-4xl leading-tight font-semibold tracking-tight text-slate-900 md:text-6xl"
        >
          {headline}
        </m.h1>
        <m.p
          initial={initial}
          animate={animate}
          transition={{ duration: 0.45, delay: 0.2 }}
          className="mt-5 max-w-2xl text-base text-slate-600 md:text-lg"
        >
          {t('subheadline')}
        </m.p>
        <m.div
          initial={initial}
          animate={animate}
          transition={{ duration: 0.45, delay: 0.3 }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          <Link
            href={`${localePrefix}/signup`}
            data-testid="hero-cta-primary"
            onClick={() => trackCta('hero-primary')}
            className="inline-flex items-center rounded-md bg-emerald-700 px-5 py-3 text-sm font-medium text-white hover:bg-emerald-800"
          >
            {t('ctaPrimary')}
          </Link>
          <Link
            href={`${localePrefix}/signup?plan=titan`}
            data-testid="hero-cta-secondary"
            onClick={() => trackCta('hero-secondary')}
            className="inline-flex items-center rounded-md border border-emerald-700 px-5 py-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
          >
            {t('ctaSecondary')}
          </Link>
        </m.div>
      </div>
    </section>
  );
}
